/**
 * Implementação remota do repositório de Notificações
 */

import type { NotificacaoRepository } from '../core/notificacao_repository';
import type { 
  Notificacao, 
  NotificacaoCompacta, 
  AdicionarNotificacaoRequest,
  AtualizarNotificacaoRequest,
  ReportarProblemaRequest
} from '../core/models';
import { RemoteDataSource } from '@/datasources/remote_datasource';

export class RemoteNotificacaoRepository implements NotificacaoRepository {
  private dataSource: RemoteDataSource;

  constructor(dataSource: RemoteDataSource) {
    this.dataSource = dataSource;
  }

  async adicionarNotificacao(request: AdicionarNotificacaoRequest): Promise<Notificacao> {
    const response = await this.dataSource.post<Notificacao>({
      endpoint: 'v1/notificacoes',
      body: request as unknown as Record<string, unknown>,
      headers: { accept: 'application/vnd.data.full.v1+json' },
      serialize: (data) => data as Notificacao,
    });
    
    const mapped = this.dataSource.map(response);
    if ('failure' in mapped) {
      throw new Error(mapped.failure.messages.join(', '));
    }
    return mapped.success;
  }

  async obterNotificacoes(compacto = false, pageNumber = 1, pageSize = 10): Promise<Notificacao[] | NotificacaoCompacta[]> {
    const accept = compacto 
      ? 'application/vnd.data.compact.v1+json'
      : 'application/vnd.data.full.v1+json';
    
    const response = await this.dataSource.get<Notificacao[] | NotificacaoCompacta[]>({
      endpoint: 'v1/notificacoes',
      headers: { accept },
      serialize: (data) => data as Notificacao[] | NotificacaoCompacta[],
      forceRefresh: true,
      queryParams: {
        PageNumber: pageNumber,
        PageSize: pageSize,
      },
    });
    
    const mapped = this.dataSource.map(response);
    if ('failure' in mapped) {
      throw new Error(mapped.failure.messages.join(', '));
    }
    return mapped.success;
  }

  async obterNotificacaoPorId(
    notificacaoId: string, 
    compacto = false
  ): Promise<Notificacao | NotificacaoCompacta> {
    const accept = compacto 
      ? 'application/vnd.data.compact.v1+json'
      : 'application/vnd.data.full.v1+json';
    
    const response = await this.dataSource.get<Notificacao | NotificacaoCompacta>({
      endpoint: `v1/notificacoes/${notificacaoId}`,
      headers: { accept },
      serialize: (data) => data as Notificacao | NotificacaoCompacta,
      forceRefresh: true,
    });
    
    const mapped = this.dataSource.map(response);
    if ('failure' in mapped) {
      throw new Error(mapped.failure.messages.join(', '));
    }
    return mapped.success;
  }

  async marcarComoLida(notificacaoId: string): Promise<void> {
    const response = await this.dataSource.post({
      endpoint: `v1/notificacoes/${notificacaoId}/ler`,
      body: {},
      headers: { accept: 'application/vnd.data.full.v1+json' },
    });
    
    if ('failure' in response) {
      throw new Error(response.failure.messages.join(', '));
    }
  }

  async atualizarNotificacao(
    notificacaoId: string, 
    request: AtualizarNotificacaoRequest
  ): Promise<Notificacao> {
    const response = await this.dataSource.put<Notificacao>({
      endpoint: `v1/notificacoes/${notificacaoId}`,
      body: request as unknown as Record<string, unknown>,
      headers: { accept: 'application/vnd.data.full.v1+json' },
      serialize: (data) => data as Notificacao,
    });
    
    const mapped = this.dataSource.map(response);
    if ('failure' in mapped) {
      throw new Error(mapped.failure.messages.join(', '));
    }
    return mapped.success;
  }

  async atualizarNotificacaoParcial(
    notificacaoId: string, 
    request: Partial<AtualizarNotificacaoRequest>
  ): Promise<Notificacao> {
    // PATCH não está implementado no RemoteDataSource, usando PUT como alternativa
    const response = await this.dataSource.put<Notificacao>({
      endpoint: `v1/notificacoes/${notificacaoId}`,
      body: request as unknown as Record<string, unknown>,
      headers: { accept: 'application/vnd.data.full.v1+json' },
      serialize: (data) => data as Notificacao,
    });
    
    const mapped = this.dataSource.map(response);
    if ('failure' in mapped) {
      throw new Error(mapped.failure.messages.join(', '));
    }
    return mapped.success;
  }

  async removerNotificacao(notificacaoId: string): Promise<void> {
    const response = await this.dataSource.delete({
      endpoint: `v1/notificacoes/${notificacaoId}`,
      headers: { accept: 'application/vnd.data.full.v1+json' },
    });
    
    if ('failure' in response) {
      throw new Error(response.failure.messages.join(', '));
    }
  }

  async reportarProblema(request: ReportarProblemaRequest): Promise<Notificacao> {
    const response = await this.dataSource.post<Notificacao>({
      endpoint: 'v1/notificacoes/reportar-problema',
      body: request as unknown as Record<string, unknown>,
      headers: { accept: 'application/vnd.data.full.v1+json' },
      serialize: (data) => data as Notificacao,
    });
    
    const mapped = this.dataSource.map(response);
    if ('failure' in mapped) {
      throw new Error(mapped.failure.messages.join(', '));
    }
    return mapped.success;
  }
}
